import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { storyService } from '../services/storyService';
import { TrashIcon, FilmIcon, PhotoIcon, EyeIcon } from '@heroicons/react/24/outline';
import Pagination from '../../../components/common/Pagination';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import DeleteConfirmation from '../../../components/common/DeleteConfirmation';
import StoryModal from './StoryModal';
import StoryViewersModal from './StoryViewersModal';

const ownerLabel = (story) => {
  if (story.is_admin_post) return 'Afrikraaga (admin)';
  if (!story.owner) return 'Propriétaire supprimé';
  return story.owner.name || story.owner.title || 'Boutique';
};

const audienceLabel = (target) => {
  switch (target) {
    case 'shop':
      return 'Boutiques';
    case 'service':
      return 'Services';
    default:
      return 'Les deux';
  }
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StoryList = () => {
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [viewersStory, setViewersStory] = useState(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['stories', page],
    queryFn: () => storyService.getStories({ page }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => storyService.deleteStory(id),
    onSuccess: () => {
      refetch();
      setShowDeleteModal(false);
    },
  });

  const handleDelete = (story) => {
    setSelectedStory(story);
    setShowDeleteModal(true);
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div className="text-red-500">Erreur lors du chargement des stories</div>;

  const stories = data?.data || [];
  const pagination = data?.meta || {};

  return (
    <div className="bg-white min-h-full shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Stories</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Modération des stories publiées par les boutiques et services, et publication de stories admin.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition whitespace-nowrap"
        >
          Publier une story
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Aperçu</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Propriétaire</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Publiée le</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Expire le</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          {stories.length > 0 ? (
            <tbody className="bg-white divide-y divide-gray-200">
              {stories.map((story) => (
                <tr key={story.id}>
                  <td className="px-4 py-3">
                    {story.media_type === 'image' ? (
                      <img src={story.media_url} alt="" className="h-14 w-14 rounded-md object-cover" />
                    ) : (
                      <div className="h-14 w-14 rounded-md bg-gray-800 flex items-center justify-center">
                        <FilmIcon className="h-6 w-6 text-white" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {story.is_admin_post && (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 mr-2 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          Admin
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 mr-2 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {audienceLabel(story.admin_target)}
                        </span>
                      </>
                    )}
                    {ownerLabel(story)}
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">
                    <span className="inline-flex items-center gap-1">
                      {story.media_type === 'image' ? (
                        <PhotoIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <FilmIcon className="h-4 w-4 text-gray-400" />
                      )}
                      {story.media_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatDateTime(story.created_at)}</td>
                  <td className="px-4 py-3 text-sm">{formatDateTime(story.expires_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {story.is_admin_post && (
                      <button
                        title="Voir les vues"
                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                        onClick={() => setViewersStory(story)}
                      >
                        <EyeIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    )}
                    <button
                      title="Supprimer"
                      className="p-2 hover:bg-red-100 rounded cursor-pointer"
                      onClick={() => handleDelete(story)}
                    >
                      <TrashIcon className="h-5 w-5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td colSpan="6">
                  <div className="flex justify-center items-center py-10 text-gray-500 text-center text-sm">
                    Aucune story active pour le moment.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      <DeleteConfirmation
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMutation.mutate(selectedStory?.id)}
        title="Supprimer la story"
        message={`Êtes-vous sûr de vouloir supprimer cette story de "${ownerLabel(selectedStory || {})}" ?`}
        confirmButtonText="Supprimer"
        confirmButtonColor="red"
      />

      <StoryModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          refetch();
          setShowCreateModal(false);
        }}
      />

      {viewersStory && (
        <StoryViewersModal story={viewersStory} onClose={() => setViewersStory(null)} />
      )}
    </div>
  );
};

export default StoryList;
